import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RectAreaLightUniformsLib } from 'three/addons/lights/RectAreaLightUniformsLib.js';
import type { CameraCalibrationMessage, CaptureStatusMessage, PackedLayoutMessage, PoseFrameMessage, PoseSource } from '../protocol.ts';
import { estimatePersonPlacement, SHOULDER_WIDTH_PRIOR_M } from '../pose/personPlacement.ts';
import { OneEuroFilter } from '../pose/oneEuro.ts';
import { epochNow } from '../latency/clock.ts';
import type { LatencyTracker } from '../latency/tracker.ts';
import { PosePathTracker } from '../latency/posePaths.ts';
import { FloorPoseDeriver } from '../media/floorPose.ts';
import { normalizePackedGeometry, packedGeometry } from '../media/packed.ts';

export interface PersonStage {
  setCalibration(calibration: CameraCalibrationMessage): void;
  setStream(stream: MediaStream): void;
  updatePose(frame: PoseFrameMessage, source?: PoseSource): void;
  setPoseSourcePending(source: PoseSource): void;
  setPoseSourceApplied(source: PoseSource): void;
  setCaptureStatus(status: CaptureStatusMessage): void;
  setPackedLayout(layout: PackedLayoutMessage): void;
  setBoardCanvas(canvas: HTMLCanvasElement): void;
  stop(reason: string): void;
  dispose(): void;
}

type CameraPreset = 'behind' | 'tv' | 'free';

export interface PersonStageOptions {
  latency?: LatencyTracker;
  floorClockOffsetMs?: () => number;
  floorClockSynchronized?: () => boolean;
  onPoseSourceChange?: (source: PoseSource) => void;
}

const disposeObject = (object: THREE.Object3D): void => {
  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    child.geometry.dispose();
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material) => material.dispose());
  });
};

export function mountPersonStage(container: HTMLElement, options: PersonStageOptions = {}): PersonStage {
  container.innerHTML = `
    <section class="person-stage-card">
      <div class="person-stage-heading"><div><p class="eyebrow">PERSON → FLOOR</p><h2>Live metric room</h2></div><span data-person-state>Waiting for CAMERA</span></div>
      <div class="room-controls">
        <label>Width <input data-room-width type="number" min="2" max="20" step=".1" value="5.5"> m</label>
        <label>Depth <input data-room-depth type="number" min="2" max="20" step=".1" value="5.5"> m</label>
        <label>Height <input data-room-height type="number" min="2" max="6" step=".1" value="2.7"> m</label>
        <label>TV bottom <input data-tv-bottom type="number" min=".2" max="2" step=".05" value=".75"> m</label>
        <label>Couch distance <input data-couch-distance type="number" min=".8" max="12" step=".1" value="2.6"> m</label>
      </div>
      <div class="camera-presets" role="group" aria-label="Floor camera preset">
        <button type="button" data-preset="behind" aria-pressed="true">Behind couch</button>
        <button type="button" data-preset="tv" aria-pressed="false">From TV</button>
        <button type="button" data-preset="free" aria-pressed="false">Free</button>
        <label class="mirror-toggle"><input type="checkbox" data-mirror> Mirror <small>3 levels</small></label>
      </div>
      <fieldset class="pose-source-control">
        <legend>Pose source</legend>
        <label><input type="radio" name="pose-source" value="data-channel" checked> Camera landmarks · data channel</label>
        <label><input type="radio" name="pose-source" value="laptop-rederives"> Laptop re-derives · matte only</label>
        <output data-pose-source-state data-pose-source="switching">Waiting for private peer control</output>
        <div class="pose-path-measurements" data-pose-path-measurements>
          <span>Data channel <b data-pose-data>—</b></span>
          <span>Laptop re-derives <b data-pose-laptop>—</b></span>
          <span>Laptop inference <b data-pose-inference>—</b></span>
          <span>Signed laptop − data <b data-pose-delta>—</b></span>
        </div>
      </fieldset>
      <div class="person-stage-view"><canvas data-person-stage aria-label="Three-dimensional preview of the room, shared TV, physical camera and live person"></canvas></div>
      <p data-person-detail>The shoulder-width estimate is a ${SHOULDER_WIDTH_PRIOR_M.toFixed(2)} m prior, cross-checked against the floor plane. It is not a body measurement.</p>
      <video data-packed-video muted playsinline hidden></video>
    </section>`;
  const canvas = container.querySelector<HTMLCanvasElement>('[data-person-stage]')!;
  const video = container.querySelector<HTMLVideoElement>('[data-packed-video]')!;
  const state = container.querySelector<HTMLElement>('[data-person-state]')!;
  const detail = container.querySelector<HTMLElement>('[data-person-detail]')!;
  const widthInput = container.querySelector<HTMLInputElement>('[data-room-width]')!;
  const depthInput = container.querySelector<HTMLInputElement>('[data-room-depth]')!;
  const heightInput = container.querySelector<HTMLInputElement>('[data-room-height]')!;
  const bottomInput = container.querySelector<HTMLInputElement>('[data-tv-bottom]')!;
  const couchInput = container.querySelector<HTMLInputElement>('[data-couch-distance]')!;
  const mirrorInput = container.querySelector<HTMLInputElement>('[data-mirror]')!;
  const poseSourceState = container.querySelector<HTMLOutputElement>('[data-pose-source-state]')!;
  const poseMeasurements = container.querySelector<HTMLElement>('[data-pose-path-measurements]')!;

  RectAreaLightUniformsLib.init();
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.setPixelRatio(Math.min(devicePixelRatio || 1, 1.5));
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x111615);
  const camera = new THREE.PerspectiveCamera(48, 1, 0.05, 40);
  const orbit = new OrbitControls(camera, canvas);
  orbit.enableDamping = true;
  orbit.maxDistance = 14;
  orbit.minDistance = 0.3;
  scene.add(new THREE.HemisphereLight(0xcad7d1, 0x403930, 0.45));
  const roomFill = new THREE.DirectionalLight(0xffe8c7, 0.35);
  roomFill.position.set(-2, 4, 4);
  scene.add(roomFill);

  let tvWidthM = 1.43;
  let tvHeightM = 0.80;
  let roomWidthM = 5.5;
  let roomDepthM = 5.5;
  let roomHeightM = 2.7;
  let tvBottomM = 0.75;
  let couchDistanceM = 2.6;
  let preset: CameraPreset = 'behind';
  let mirrorEnabled = false;

  const floorMaterial = new THREE.MeshStandardMaterial({ color: 0x665545, roughness: 0.86 });
  const wallMaterial = new THREE.MeshStandardMaterial({ color: 0xd4d0c8, roughness: 0.94, side: THREE.DoubleSide });
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), floorMaterial);
  floor.rotation.x = -Math.PI / 2;
  scene.add(floor);
  const backWall = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), wallMaterial);
  scene.add(backWall);
  const sideWallMaterial = wallMaterial.clone();
  const leftWall = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), sideWallMaterial);
  leftWall.rotation.y = Math.PI / 2;
  scene.add(leftWall);
  const rightWall = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), sideWallMaterial);
  rightWall.rotation.y = -Math.PI / 2;
  scene.add(rightWall);
  const grid = new THREE.GridHelper(1, 20, 0x83948b, 0x3b443f);
  scene.add(grid);

  const couchMaterial = new THREE.MeshStandardMaterial({ color: 0x596c63, roughness: 0.98 });
  const couch = new THREE.Group();
  const couchPart = (width: number, height: number, depth: number, x: number, y: number, z: number): void => {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), couchMaterial);
    mesh.position.set(x, y, z);
    couch.add(mesh);
  };
  couchPart(2.25, 0.34, 0.9, 0, 0.34, 0);
  couchPart(2.25, 0.82, 0.20, 0, 0.78, 0.38);
  couchPart(0.18, 0.52, 0.92, -1.12, 0.48, 0);
  couchPart(0.18, 0.52, 0.92, 1.12, 0.48, 0);
  scene.add(couch);

  const tvFrameMaterial = new THREE.MeshStandardMaterial({ color: 0x161819, roughness: 0.3, metalness: 0.5 });
  const tvFrame = new THREE.Mesh(new THREE.BoxGeometry(tvWidthM + 0.05, tvHeightM + 0.05, 0.035), tvFrameMaterial);
  const tvMaterial = new THREE.MeshBasicMaterial({ color: 0x273b35 });
  const tvScreen = new THREE.Mesh(new THREE.PlaneGeometry(tvWidthM, tvHeightM), tvMaterial);
  tvScreen.position.z = 0.02;
  scene.add(tvFrame, tvScreen);
  const screenLight = new THREE.RectAreaLight(0xffffff, 200, tvWidthM, tvHeightM);
  screenLight.position.set(0, 0, 0.045);
  screenLight.lookAt(0, 0, 1);
  scene.add(screenLight);

  const cameraMarker = new THREE.Group();
  const cameraBody = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.09, 0.09), new THREE.MeshStandardMaterial({ color: 0xe2bb68, roughness: 0.35 }));
  const lens = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.035, 18), new THREE.MeshStandardMaterial({ color: 0x101112, roughness: 0.2 }));
  lens.rotation.x = Math.PI / 2;
  lens.position.z = -0.06;
  cameraMarker.add(cameraBody, lens);
  cameraMarker.visible = false;
  scene.add(cameraMarker);

  const texture = new THREE.VideoTexture(video);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  const defaultPackedLayout: PackedLayoutMessage = {
    kind: 'packed-layout',
    revision: 0,
    ...normalizePackedGeometry(packedGeometry(640, 360, 'side-by-side')),
  };
  let packedLayout = defaultPackedLayout;
  const personMaterial = new THREE.ShaderMaterial({
    uniforms: {
      packedFrame: { value: texture },
      tvLight: { value: new THREE.Color(1, 1, 1) },
      colourRect: { value: new THREE.Vector4(defaultPackedLayout.colour.x, defaultPackedLayout.colour.y, defaultPackedLayout.colour.width, defaultPackedLayout.colour.height) },
      alphaRect: { value: new THREE.Vector4(defaultPackedLayout.alpha.x, defaultPackedLayout.alpha.y, defaultPackedLayout.alpha.width, defaultPackedLayout.alpha.height) },
      alphaAtlas: { value: 0 },
    },
    transparent: true,
    premultipliedAlpha: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    toneMapped: false,
    vertexShader: `varying vec2 captureUv; void main(){captureUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`,
    fragmentShader: `uniform sampler2D packedFrame;uniform vec3 tvLight;uniform vec4 colourRect;uniform vec4 alphaRect;uniform float alphaAtlas;varying vec2 captureUv;
      void main(){vec3 colour=texture2D(packedFrame,colourRect.xy+captureUv*colourRect.zw).rgb;
      vec2 matteUv=captureUv;if(alphaAtlas>.5){matteUv=captureUv.y<.5
      ?vec2(captureUv.x*.5,captureUv.y*2.):vec2(.5+captureUv.x*.5,(captureUv.y-.5)*2.);}
      float alpha=texture2D(packedFrame,alphaRect.xy+matteUv*alphaRect.zw).r;
      if(alpha<.015)discard;colour*=.78+tvLight*.22;gl_FragColor=vec4(colour,alpha);}`,
  });
  const billboard = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), personMaterial);
  billboard.visible = false;
  billboard.rotation.y = Math.PI;
  scene.add(billboard);

  const newFilters = () => ({
    x: new OneEuroFilter(), y: new OneEuroFilter(), z: new OneEuroFilter(),
    width: new OneEuroFilter(), height: new OneEuroFilter(),
  });
  let filters = newFilters();
  const averageCanvas = document.createElement('canvas');
  averageCanvas.width = 16;
  averageCanvas.height = 9;
  const averageContext = averageCanvas.getContext('2d', { willReadFrequently: true })!;
  const runningScreenColour = new THREE.Color(1, 1, 1);
  let calibration: CameraCalibrationMessage | undefined;
  let boardTexture: THREE.CanvasTexture | undefined;
  let lastSequence = -1;
  let videoFrameCallback = 0;
  let latestVideoCapturePerformanceMs: number | undefined;
  let latestVideoCaptureAnchor: 'capture-time' | 'decoded-frame' = 'decoded-frame';
  let pendingCapturePerformanceMs: number | undefined;
  let pendingCaptureServerEpochMs: number | undefined;
  let pendingPoseSource: PoseSource | undefined;
  let pendingLatencyAnchor: 'capture-time' | 'decoded-frame' | 'clock-offset' | undefined;
  let lastRenderTimestamp: number | undefined;
  let desiredPoseSource: PoseSource = 'data-channel';
  let appliedPoseSource: PoseSource | undefined;
  let latestCaptureStatus: CaptureStatusMessage | undefined;
  let lastCaptureStatusSequence = -1;
  const posePaths = new PosePathTracker();
  const inferenceTimes = new PosePathTracker();
  const floorClockOffsetMs = options.floorClockOffsetMs ?? (() => 0);
  const floorClockSynchronized = options.floorClockSynchronized ?? (() => false);
  const mirrorTargets = [0, 1].map(() => {
    const target = new THREE.WebGLRenderTarget(2, 2, { depthBuffer: true });
    target.texture.colorSpace = THREE.SRGBColorSpace;
    target.texture.minFilter = target.texture.magFilter = THREE.LinearFilter;
    target.texture.generateMipmaps = false;
    return target;
  });

  const renderPathMeasurements = (): void => {
    const data = posePaths.summary('data-channel');
    const laptop = posePaths.summary('laptop-rederives');
    const inference = inferenceTimes.summary('laptop-rederives');
    const delta = posePaths.delta();
    const show = (summary: { count: number; p50?: number; p95?: number }): string => summary.p50 === undefined
      ? '—'
      : `p50 ${summary.p50.toFixed(1)} · p95 ${summary.p95!.toFixed(1)} ms · ${summary.count}`;
    container.querySelector<HTMLElement>('[data-pose-data]')!.textContent = show(data);
    container.querySelector<HTMLElement>('[data-pose-laptop]')!.textContent = show(laptop);
    container.querySelector<HTMLElement>('[data-pose-inference]')!.textContent = show(inference);
    container.querySelector<HTMLElement>('[data-pose-delta]')!.textContent = delta.p50 === undefined
      ? '—'
      : `p50 ${delta.p50! >= 0 ? '+' : ''}${delta.p50!.toFixed(1)} · p95 ${delta.p95! >= 0 ? '+' : ''}${delta.p95!.toFixed(1)} ms`;
    poseMeasurements.dataset.poseDataCount = String(data.count);
    poseMeasurements.dataset.poseLaptopCount = String(laptop.count);
    poseMeasurements.dataset.poseInferenceCount = String(inference.count);
    for (const [name, value] of Object.entries({
      poseDataP50Ms: data.p50,
      poseDataP95Ms: data.p95,
      poseLaptopP50Ms: laptop.p50,
      poseLaptopP95Ms: laptop.p95,
      poseInferenceP50Ms: inference.p50,
      poseInferenceP95Ms: inference.p95,
      poseDeltaP50Ms: delta.p50,
      poseDeltaP95Ms: delta.p95,
    })) {
      if (value === undefined) delete poseMeasurements.dataset[name];
      else poseMeasurements.dataset[name] = value.toFixed(3);
    }
  };
  renderPathMeasurements();

  const floorY = (): number => -tvHeightM / 2 - tvBottomM;

  const applyPreset = (next: CameraPreset = preset): void => {
    preset = next;
    container.querySelectorAll<HTMLButtonElement>('[data-preset]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.preset === preset)));
    orbit.enabled = preset === 'free';
    if (preset === 'behind') {
      camera.position.set(0, floorY() + 1.55, Math.min(roomDepthM - 0.25, couchDistanceM + 1.7));
      orbit.target.set(0, floorY() + 1.05, 0.15);
      camera.lookAt(orbit.target);
    } else if (preset === 'tv') {
      camera.position.set(0, 0.05, 0.24);
      orbit.target.set(0, floorY() + 0.85, Math.min(roomDepthM - 0.2, couchDistanceM));
      camera.lookAt(orbit.target);
    }
    orbit.update();
  };

  const updateRoom = (): void => {
    roomWidthM = Math.max(2, Number(widthInput.value) || 5.5);
    roomDepthM = Math.max(2, Number(depthInput.value) || 5.5);
    roomHeightM = Math.max(2, Number(heightInput.value) || 2.7);
    tvBottomM = Math.max(0.2, Number(bottomInput.value) || 0.75);
    couchDistanceM = Math.min(roomDepthM - 0.5, Math.max(0.8, Number(couchInput.value) || 2.6));
    const y = floorY();
    floor.scale.set(roomWidthM, roomDepthM, 1);
    floor.position.set(0, y, roomDepthM / 2);
    backWall.scale.set(roomWidthM, roomHeightM, 1);
    backWall.position.set(0, y + roomHeightM / 2, -0.035);
    leftWall.scale.set(roomDepthM, roomHeightM, 1);
    leftWall.position.set(-roomWidthM / 2, y + roomHeightM / 2, roomDepthM / 2);
    rightWall.scale.set(roomDepthM, roomHeightM, 1);
    rightWall.position.set(roomWidthM / 2, y + roomHeightM / 2, roomDepthM / 2);
    grid.scale.set(roomWidthM, 1, roomDepthM);
    grid.position.set(0, y + 0.003, roomDepthM / 2);
    couch.position.set(0, y, couchDistanceM);
    applyPreset();
  };
  for (const input of [widthInput, depthInput, heightInput, bottomInput, couchInput]) input.addEventListener('input', updateRoom);
  container.querySelectorAll<HTMLButtonElement>('[data-preset]').forEach((button) => button.addEventListener('click', () => applyPreset(button.dataset.preset as CameraPreset)));

  const resize = (): void => {
    const bounds = canvas.getBoundingClientRect();
    const width = Math.max(2, Math.round(bounds.width));
    const height = Math.max(2, Math.round(bounds.height));
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    const targetWidth = Math.min(1024, Math.max(2, width));
    const targetHeight = Math.min(576, Math.max(2, Math.round(targetWidth / camera.aspect)));
    mirrorTargets.forEach((target) => target.setSize(targetWidth, targetHeight));
    camera.updateProjectionMatrix();
  };
  const observer = new ResizeObserver(resize);
  observer.observe(canvas);
  updateRoom();
  resize();
  mirrorInput.addEventListener('change', () => {
    mirrorEnabled = mirrorInput.checked;
    options.latency?.clear('frame');
    container.dataset.mirror = mirrorEnabled ? 'on' : 'off';
    container.dataset.mirrorDepth = mirrorEnabled ? '3' : '0';
    if (!mirrorEnabled) {
      const mapPresenceChanged = Boolean(tvMaterial.map) !== Boolean(boardTexture);
      tvMaterial.map = boardTexture ?? null;
      if (mapPresenceChanged) tvMaterial.needsUpdate = true;
    }
  });
  renderer.setAnimationLoop((timestamp) => {
    if (lastRenderTimestamp !== undefined) {
      const interval = timestamp - lastRenderTimestamp;
      if (interval > 0 && interval < 250) options.latency?.add('frame', interval);
    }
    lastRenderTimestamp = timestamp;
    orbit.update();
    if (mirrorEnabled) {
      // Each target samples only the previous completed pass. A/B ping-pong means the
      // active framebuffer is never its own texture; rebuilding from the board each frame
      // fixes the Droste depth at exactly three instead of letting feedback grow forever.
      let previous: THREE.Texture | null = boardTexture ?? null;
      for (let depth = 0; depth < 3; depth += 1) {
        const target = mirrorTargets[depth % 2]!;
        const mapPresenceChanged = Boolean(tvMaterial.map) !== Boolean(previous);
        tvMaterial.map = previous;
        if (mapPresenceChanged) tvMaterial.needsUpdate = true;
        renderer.setRenderTarget(target);
        renderer.render(scene, camera);
        previous = target.texture;
      }
      const mapPresenceChanged = Boolean(tvMaterial.map) !== Boolean(previous);
      tvMaterial.map = previous;
      if (mapPresenceChanged) tvMaterial.needsUpdate = true;
    }
    renderer.setRenderTarget(null);
    renderer.render(scene, camera);
    if (pendingCapturePerformanceMs !== undefined) {
      const latencyMs = performance.now() - pendingCapturePerformanceMs;
      options.latency?.add('person', latencyMs);
      if (pendingPoseSource) posePaths.add(pendingPoseSource, latencyMs);
      renderPathMeasurements();
      pendingCapturePerformanceMs = undefined;
      pendingCaptureServerEpochMs = undefined;
      pendingPoseSource = undefined;
      container.dataset.personLatencySource = pendingLatencyAnchor ?? 'decoded-frame';
      pendingLatencyAnchor = undefined;
    } else if (pendingCaptureServerEpochMs !== undefined && floorClockSynchronized()) {
      const latencyMs = epochNow() + floorClockOffsetMs() - pendingCaptureServerEpochMs;
      options.latency?.add('person', latencyMs);
      if (pendingPoseSource) posePaths.add(pendingPoseSource, latencyMs);
      renderPathMeasurements();
      pendingCaptureServerEpochMs = undefined;
      pendingPoseSource = undefined;
      container.dataset.personLatencySource = 'landmark-clock-offset';
      pendingLatencyAnchor = undefined;
    }
  });

  const setCalibration = (next: CameraCalibrationMessage): void => {
    calibration = next;
    tvWidthM = next.tv.panelWidthM;
    tvHeightM = next.tv.panelHeightM;
    tvFrame.geometry.dispose();
    tvFrame.geometry = new THREE.BoxGeometry(tvWidthM + 0.05, tvHeightM + 0.05, 0.035);
    tvScreen.geometry.dispose();
    tvScreen.geometry = new THREE.PlaneGeometry(tvWidthM, tvHeightM);
    screenLight.width = tvWidthM;
    screenLight.height = tvHeightM;
    updateRoom();
    const position = next.camera.cameraPositionInTv;
    cameraMarker.position.set(position.x, position.y, position.z);
    const r = next.camera.rotationCameraToTv;
    const orientation = new THREE.Matrix4().set(
      r[0], -r[1], -r[2], 0,
      r[3], -r[4], -r[5], 0,
      r[6], -r[7], -r[8], 0,
      0, 0, 0, 1,
    );
    cameraMarker.quaternion.setFromRotationMatrix(orientation);
    cameraMarker.visible = true;
    state.textContent = `${next.installation.label} · waiting for one person`;
  };

  const cancelVideoMeasurement = (): void => {
    if (videoFrameCallback && 'cancelVideoFrameCallback' in video) video.cancelVideoFrameCallback(videoFrameCallback);
    videoFrameCallback = 0;
  };

  const scheduleVideoMeasurement = (): void => {
    if (!video.srcObject || !('requestVideoFrameCallback' in video)) return;
    videoFrameCallback = video.requestVideoFrameCallback((now, metadata) => {
      videoFrameCallback = 0;
      const captureTime = metadata.captureTime;
      const localCaptureTime = typeof captureTime === 'number' && Number.isFinite(captureTime)
        ? (captureTime > 1_000_000_000_000 ? captureTime - performance.timeOrigin : captureTime)
        : undefined;
      const metadataAge = localCaptureTime === undefined ? Number.NaN : now - localCaptureTime;
      if (metadataAge >= 0 && metadataAge <= 10_000) {
        latestVideoCapturePerformanceMs = localCaptureTime;
        latestVideoCaptureAnchor = 'capture-time';
      } else {
        latestVideoCapturePerformanceMs = now;
        latestVideoCaptureAnchor = 'decoded-frame';
      }
      scheduleVideoMeasurement();
    });
  };

  const setStream = (stream: MediaStream): void => {
    cancelVideoMeasurement();
    video.srcObject = stream;
    scheduleVideoMeasurement();
    if (appliedPoseSource === 'laptop-rederives') void deriver.start();
    void video.play().catch(() => {
      state.textContent = 'Press the page once to allow the live preview';
      const resume = (): void => { void video.play(); window.removeEventListener('pointerdown', resume); };
      window.addEventListener('pointerdown', resume);
    });
  };

  const updatePose = (frame: PoseFrameMessage, source: PoseSource = 'data-channel'): void => {
    if (source !== appliedPoseSource) return;
    if (source === 'laptop-rederives' && latestCaptureStatus?.status !== 'live') return;
    if (frame.sequence <= lastSequence) return;
    lastSequence = frame.sequence;
    if (frame.status !== 'live') {
      billboard.visible = false;
      container.dataset.personBillboard = 'hidden';
      state.textContent = frame.reason ?? 'Camera paused';
      return;
    }
    if (!calibration) {
      billboard.visible = false;
      container.dataset.personBillboard = 'hidden';
      state.textContent = 'Live matte received · calibrate the camera to place it';
      return;
    }
    const placement = estimatePersonPlacement(frame, calibration, tvBottomM);
    if (!placement) {
      billboard.visible = false;
      container.dataset.personBillboard = 'hidden';
      state.textContent = 'Pose uncertain: paused';
      return;
    }
    const time = frame.captureEpochMs / 1000;
    billboard.position.set(
      filters.x.update(placement.billboardCenterInTv.x, time),
      filters.y.update(placement.billboardCenterInTv.y, time),
      filters.z.update(placement.billboardCenterInTv.z, time),
    );
    billboard.scale.set(
      filters.width.update(placement.billboardWidthM, time),
      filters.height.update(placement.billboardHeightM, time),
      1,
    );
    billboard.visible = true;
    container.dataset.personBillboard = 'visible';
    container.dataset.personX = placement.hipInTv.x.toFixed(3);
    container.dataset.personY = placement.hipInTv.y.toFixed(3);
    container.dataset.personZ = placement.hipInTv.z.toFixed(3);
    state.textContent = `Live · ${placement.posture} · ${placement.depthCameraZM.toFixed(2)} m from camera`;
    detail.textContent = `${SHOULDER_WIDTH_PRIOR_M.toFixed(2)} m shoulder-width prior${placement.usedFloorCrossCheck ? ', agreed with floor-plane cross-check' : '; floor cross-check disagreed or was unavailable, so it was not used'}. No frame is buffered by the one-euro smoother.`;
    const now = performance.now();
    const localCapture = source === 'laptop-rederives'
      ? frame.capturePerformanceMs
      : latestVideoCapturePerformanceMs;
    if (localCapture !== undefined && now - localCapture >= 0 && now - localCapture <= 10_000) {
      pendingCapturePerformanceMs = localCapture;
      pendingCaptureServerEpochMs = undefined;
      pendingPoseSource = source;
      pendingLatencyAnchor = source === 'data-channel' ? latestVideoCaptureAnchor : 'capture-time';
      if (source === 'data-channel') latestVideoCapturePerformanceMs = undefined;
    } else if (
      source === 'data-channel'
      && !('requestVideoFrameCallback' in video)
      && frame.clockSynchronized
      && floorClockSynchronized()
    ) {
      pendingCapturePerformanceMs = undefined;
      pendingCaptureServerEpochMs = frame.captureEpochMs + frame.clockOffsetMs;
      pendingPoseSource = source;
      pendingLatencyAnchor = 'clock-offset';
    }
  };

  const deriver = new FloorPoseDeriver({
    video,
    calibration: () => calibration,
    packedLayout: () => packedLayout,
    onPose: (frame) => updatePose(frame, 'laptop-rederives'),
    onInference: (milliseconds) => {
      inferenceTimes.add('laptop-rederives', milliseconds);
      renderPathMeasurements();
    },
    onError: (reason) => {
      billboard.visible = false;
      container.dataset.personBillboard = 'hidden';
      state.textContent = `Laptop pose paused · ${reason}`;
    },
  });

  const resetPoseState = (): void => {
    lastSequence = -1;
    filters = newFilters();
    pendingCapturePerformanceMs = undefined;
    pendingCaptureServerEpochMs = undefined;
    pendingPoseSource = undefined;
    pendingLatencyAnchor = undefined;
    billboard.visible = false;
    container.dataset.personBillboard = 'hidden';
    options.latency?.clear('person');
  };

  const setPoseSourcePending = (source: PoseSource): void => {
    desiredPoseSource = source;
    appliedPoseSource = undefined;
    latestCaptureStatus = undefined;
    lastCaptureStatusSequence = -1;
    deriver.stop();
    resetPoseState();
    container.dataset.poseSource = 'switching';
    poseSourceState.dataset.poseSource = 'switching';
    poseSourceState.textContent = source === 'laptop-rederives'
      ? 'Switching to laptop re-derives · no landmark packets'
      : 'Switching to camera landmarks · data channel';
    state.textContent = 'Pose source switching: person hidden';
    container.querySelectorAll<HTMLInputElement>('[name="pose-source"]').forEach((input) => {
      input.checked = input.value === source;
    });
  };

  const setPoseSourceApplied = (source: PoseSource): void => {
    if (source !== desiredPoseSource) return;
    appliedPoseSource = source;
    latestCaptureStatus = undefined;
    lastCaptureStatusSequence = -1;
    resetPoseState();
    container.dataset.poseSource = source;
    poseSourceState.dataset.poseSource = source;
    poseSourceState.textContent = source === 'laptop-rederives'
      ? 'Laptop re-derives · matte track only; zero landmark packets'
      : 'Camera landmarks · private data channel';
    state.textContent = calibration ? `${calibration.installation.label} · waiting for one person` : 'Waiting for camera installation';
    if (source === 'laptop-rederives' && video.srcObject) void deriver.start();
    else deriver.stop();
  };

  const setCaptureStatus = (statusMessage: CaptureStatusMessage): void => {
    if (appliedPoseSource !== 'laptop-rederives' || statusMessage.sequence <= lastCaptureStatusSequence) return;
    lastCaptureStatusSequence = statusMessage.sequence;
    latestCaptureStatus = statusMessage;
    if (statusMessage.status === 'live') {
      state.textContent = calibration ? `${calibration.installation.label} · deriving pose on FLOOR` : 'Live matte received · calibrate the camera to place it';
      return;
    }
    billboard.visible = false;
    container.dataset.personBillboard = 'hidden';
    state.textContent = statusMessage.reason ?? (statusMessage.status === 'stopped' ? 'Camera stopped' : 'Camera paused');
  };

  const setPackedLayout = (message: PackedLayoutMessage): void => {
    if (message.revision < packedLayout.revision) return;
    packedLayout = message;
    personMaterial.uniforms.colourRect!.value.set(
      message.colour.x, message.colour.y, message.colour.width, message.colour.height,
    );
    personMaterial.uniforms.alphaRect!.value.set(
      message.alpha.x, message.alpha.y, message.alpha.width, message.alpha.height,
    );
    personMaterial.uniforms.alphaAtlas!.value = message.layout === 'alpha-strip-half' ? 1 : 0;
    container.dataset.packedLayout = message.layout;
  };

  container.querySelectorAll<HTMLInputElement>('[name="pose-source"]').forEach((input) => {
    input.addEventListener('change', () => {
      if (!input.checked) return;
      const source = input.value as PoseSource;
      setPoseSourcePending(source);
      options.onPoseSourceChange?.(source);
    });
  });

  const stop = (reason: string): void => {
    deriver.stop();
    cancelVideoMeasurement();
    pendingCapturePerformanceMs = undefined;
    pendingCaptureServerEpochMs = undefined;
    pendingPoseSource = undefined;
    pendingLatencyAnchor = undefined;
    billboard.visible = false;
    container.dataset.personBillboard = 'hidden';
    video.srcObject = null;
    state.textContent = reason;
  };

  const setBoardCanvas = (boardCanvas: HTMLCanvasElement): void => {
    if (!boardTexture || boardTexture.image !== boardCanvas) {
      boardTexture?.dispose();
      boardTexture = new THREE.CanvasTexture(boardCanvas);
      boardTexture.colorSpace = THREE.SRGBColorSpace;
      boardTexture.minFilter = boardTexture.magFilter = THREE.LinearFilter;
      boardTexture.generateMipmaps = false;
      if (!mirrorEnabled) tvMaterial.map = boardTexture;
      tvMaterial.color.set(0xffffff);
      tvMaterial.needsUpdate = true;
    }
    boardTexture.needsUpdate = true;
    averageContext.drawImage(boardCanvas, 0, 0, averageCanvas.width, averageCanvas.height);
    const pixels = averageContext.getImageData(0, 0, averageCanvas.width, averageCanvas.height).data;
    let red = 0, green = 0, blue = 0;
    for (let index = 0; index < pixels.length; index += 4) {
      red += pixels[index]!;
      green += pixels[index + 1]!;
      blue += pixels[index + 2]!;
    }
    const count = pixels.length / 4;
    const measured = new THREE.Color().setRGB(red / count / 255, green / count / 255, blue / count / 255, THREE.SRGBColorSpace);
    runningScreenColour.lerp(measured, 0.18);
    screenLight.color.copy(runningScreenColour);
    screenLight.intensity = 200;
    personMaterial.uniforms.tvLight!.value.copy(runningScreenColour);
  };

  return {
    setCalibration,
    setStream,
    updatePose,
    setPoseSourcePending,
    setPoseSourceApplied,
    setCaptureStatus,
    setPackedLayout,
    setBoardCanvas,
    stop,
    dispose: () => {
      renderer.setAnimationLoop(null);
      cancelVideoMeasurement();
      deriver.stop();
      observer.disconnect();
      orbit.dispose();
      video.srcObject = null;
      texture.dispose();
      boardTexture?.dispose();
      mirrorTargets.forEach((target) => target.dispose());
      personMaterial.dispose();
      billboard.geometry.dispose();
      disposeObject(couch);
      disposeObject(cameraMarker);
      tvFrame.geometry.dispose();
      tvScreen.geometry.dispose();
      tvFrameMaterial.dispose();
      tvMaterial.dispose();
      floor.geometry.dispose();
      floorMaterial.dispose();
      backWall.geometry.dispose();
      wallMaterial.dispose();
      leftWall.geometry.dispose();
      rightWall.geometry.dispose();
      sideWallMaterial.dispose();
      renderer.dispose();
      container.replaceChildren();
    },
  };
}
